import React, { useState, useEffect } from "react";
import {
  Typography,
  Spin,
  Row,
  Radio,
  Tooltip,
  Form,
  Select,
  Checkbox,
} from "antd";
import i18n from "i18n";
import CustomModal from "components/CustomModal";
import { LanguageModel } from "api/sms-api";
import { connect } from "react-redux";
import { RootState } from "store/store";
import { proofReadingModel } from "../../constants";

const { Paragraph } = Typography;

interface SelectGptModelModalProps {
  visible: boolean;
  closeModal: () => void;
  selectModelId: string | null;
  industryCategoryId?: string;
  industrySubcategory?: string;
  languageModel: Array<LanguageModel>;
  getLanguageModelLoading: boolean;
  isRandomGenerateModel: boolean;
  generateTemperature: number;
  onChange?: (value: boolean) => void;
  onSave: (
    selectedModelId: string | null,
    isRandom: boolean,
    isProofreading: boolean,
    generateTemperature: number
  ) => void;
}

const SelectGptModelModal: React.FC<SelectGptModelModalProps> = ({
  visible,
  closeModal,
  selectModelId,
  industryCategoryId,
  industrySubcategory,
  languageModel,
  getLanguageModelLoading,
  isRandomGenerateModel,
  generateTemperature,
  onChange,
  onSave,
}) => {
  const [localIsRandomGenerate, setLocalIsRandomGenerate] = useState(
    isRandomGenerateModel
  );
  const [localSelectedModelId, setLocalSelectedModelId] = useState<
    string | undefined
  >(selectModelId ?? undefined);
  const [localIsProofreading, setLocalIsProofreading] = useState(false);

  const [selectedModelName, setSelectedModelName] = useState<string | null>(
    null
  );
  const [
    localGenerateTemperature,
    setLocalGenerateTemperature,
  ] = useState<number>(generateTemperature);

  useEffect(() => {
    if (visible) {
      setLocalIsRandomGenerate(isRandomGenerateModel);
      setLocalSelectedModelId(selectModelId ?? undefined);
      setLocalIsProofreading(false);
      setLocalGenerateTemperature(generateTemperature);
    }
  }, [visible, isRandomGenerateModel, selectModelId, generateTemperature]);

  // 業種が選択されていない場合のindustryモデル無効化
  const options = languageModel.map(({ name, id, code }) => {
    const isIndustryModel = code && code.toLowerCase().includes("industry");
    // 業種カテゴリまたはサブカテゴリのいずれかが空の場合は無効化
    const isDisabled =
      isIndustryModel &&
      (!industryCategoryId ||
        industryCategoryId.trim() === "" ||
        !industrySubcategory ||
        industrySubcategory.trim() === "");

    return {
      label: name || "",
      value: id,
      disabled: !!isDisabled,
      isIndustryModel,
    };
  });

  useEffect(() => {
    const initialSelectedModel = options.find(
      (option) => option.value === localSelectedModelId
    );
    if (initialSelectedModel) {
      setSelectedModelName(initialSelectedModel.label);
    }
  }, [
    languageModel,
    localSelectedModelId,
    industryCategoryId,
    industrySubcategory,
  ]);

  const handleSave = () => {
    onSave(
      localSelectedModelId ?? null,
      localIsRandomGenerate,
      localIsProofreading,
      localGenerateTemperature
    );
    closeModal();
  };

  const handleCloseModal = () => {
    closeModal();
  };

  const handleModelChange = (value: string) => {
    setLocalSelectedModelId(value);
    const selectedOption = options.find((option) => option.value === value);
    setSelectedModelName(selectedOption ? selectedOption.label : null);
  };

  const handleRandomModelSelect = (value: boolean) => {
    setLocalIsRandomGenerate(value);
    onChange?.(value);
  };

  const handleProofreadingChange = (e: any) => {
    setLocalIsProofreading(e.target.checked);
  };

  return (
    <CustomModal
      title={i18n.t("survey.selectGptModel")}
      okText={i18n.t("common.save")}
      visible={visible}
      closeModal={handleCloseModal}
      onOk={handleSave}
    >
      <Form layout="vertical">
        <Form.Item>
          <Radio.Group
            style={{ display: "flex", flexDirection: "column" }}
            value={localIsRandomGenerate}
            onChange={(e) => handleRandomModelSelect(e.target.value)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              {/* ランダム生成：業種選択型でランダム生成をする可能性があるので一旦表示からは削除 */}
              {/* <Radio value={true} style={{ marginRight: "10px" }}>
                {i18n.t("survey.randomGenerateModelText")}
              </Radio> */}
              {localIsRandomGenerate && (
                <Checkbox
                  checked={localIsProofreading}
                  onChange={handleProofreadingChange}
                  style={{ paddingLeft: "20px" }}
                >
                  {i18n.t("survey.selectGptModelOption")}
                </Checkbox>
              )}
            </div>
            <Radio value={false}>{i18n.t("survey.individualModelText")}</Radio>
          </Radio.Group>
        </Form.Item>
        {!localIsRandomGenerate && (
          <>
            <Paragraph>{i18n.t("survey.selectGptModelText")}</Paragraph>
            <Row className="select-language">
              {getLanguageModelLoading ? (
                <Spin />
              ) : (
                <Select
                  value={localSelectedModelId}
                  style={{ width: "250px" }}
                  onChange={handleModelChange}
                  dropdownRender={(menu) => menu}
                >
                  {options.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.disabled ? (
                        <Tooltip
                          title={i18n.t("survey.selectIndustryCategoryTooltip")}
                          placement="right"
                        >
                          <span style={{ color: "#d9d9d9" }}>
                            {option.label}
                          </span>
                        </Tooltip>
                      ) : (
                        option.label
                      )}
                    </Select.Option>
                  ))}
                </Select>
              )}
              {selectedModelName &&
                proofReadingModel.some((model) =>
                  selectedModelName.includes(model)
                ) && (
                  <Checkbox
                    checked={localIsProofreading}
                    onChange={handleProofreadingChange}
                    style={{ paddingLeft: "20px" }}
                  >
                    {i18n.t("survey.selectGptModelOption")}
                  </Checkbox>
                )}
            </Row>
            <Row style={{ marginTop: 16 }}>
              <p>{i18n.t("survey.generateTemperatureSettingTitle")}</p>
            </Row>
            <Row>
              <Select
                value={localGenerateTemperature}
                style={{ width: 100 }}
                onChange={(value) => setLocalGenerateTemperature(value)}
              >
                {[...Array(9)].map((_, i) => {
                  const temp = (5 + i) / 10;
                  const tempValue = parseFloat(temp.toFixed(1));
                  const tempDisplay = temp.toFixed(1);
                  return (
                    <Select.Option key={tempValue} value={tempValue}>
                      {tempDisplay}
                    </Select.Option>
                  );
                })}
              </Select>
            </Row>
          </>
        )}
      </Form>
    </CustomModal>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    languageModel: state.survey.languageModel,
    isProofreading: state.survey.isProofreading,
    getLanguageModelLoading: state.survey.getLanguageModelLoading,
  };
};

const mapDispatchToProps = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SelectGptModelModal);
